import HelpCircle from "@untitled-ui/icons-react/build/esm/HelpCircle";
import InfoCircle from "@untitled-ui/icons-react/build/esm/InfoCircle";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useOutletContext } from "react-router";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import PopupContext from "../../../PopupContext";
import * as Library from "../../../utils/Library";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import { POSSetupSettingsMenuComponent } from "../stores/StoresPage";
import POSCategoriesTablePopup from "./POSExportComponents/popups/POSCategoriesTablePopup";
import POSColorsTablePopup from "./POSExportComponents/popups/POSColorsTablePopup";
import { areColorsMapped, buildColorSuggestions, mergeColorMappings } from "./POSExportComponents/colorMappingState";
import POSGendersTablePopup from "./POSExportComponents/popups/POSGendersTablePopup";
import POSSeasonsTablePopup from "./POSExportComponents/popups/POSSeasonsTablePopup";
import POSSizeGroupConversionPopup from "./POSExportComponents/popups/POSSizeGroupConversionPopup";
import {
    findCategoryMapping,
    getCategoryMappingKey,
    getSourceValueKey,
    sourceValuesMatch
} from "./POSExportComponents/sourceMappingIdentity";
import {
    buildCategorySizeSourceRows,
    expandCategoryMappingsForSizeGroups
} from "./POSExportComponents/categoryMappingState";
import {
    areMissingSizesMapped,
    getCategorySizeCountIssues,
    getMissingSizeRows,
    getSizeMappingConflicts
} from "./POSExportComponents/sizeGroupConversionState";
import POSExpInfoAvailableCard from "./POSExportComponents/POSExpInfoAvailableCard";
import POSExpPreviewCard from "./POSExportComponents/POSExpPreviewCard";
import POSExpRulesCard, { LANGUAGES_OPTIONS, ROUNDING_PRICE_OPTIONS } from "./POSExportComponents/POSExpRulesCard";

// #region SCOPED CSS
const StyledPage = styled.div`
    &.pos-export-page {
        display: flex;
        flex-direction: column;
    }

    .dropdownArrow {
        top: unset !important;
    }

    .frame-1000011192 {
        display: flex;
        width: 100%;
        flex-direction: column;
        align-items: flex-start;
        gap: 10px;
    }

    .frame-1000011467 {
        display: flex;
        align-items: flex-start;
        gap: 16px;
        align-self: stretch;
    }

    .frame-313379 {
        display: flex;
        align-items: flex-start;
        gap: 24px;
        flex: 1 0 0;
    }

    .frame-313378 {
        display: flex;
        align-items: flex-start;
        gap: 24px;
        flex: 1 0 0;
    }

    //The parent frame-1000011467 should break the child frame-313378 and frame-313379 when the screen width is less than 1900px
    ${props =>
        props.enablePageBreak &&
        `@media (max-width: 1900px) {
            .frame-1000011467 {
                flex-direction: column;
            }
        }`}

    .frame-1000011466 {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        gap: 24px;
    }

    .frame-313378 .BO-card-component,
    .frame-313379 .BO-card-component {
        margin-bottom: 0px !important;
        height: 858px; //TODO: Temporary
    }

    .no-label-brand-selected {
        color: #ffffff;
        text-align: center;
        font-size: 14px;
        font-weight: 400;
        position: relative;
        height: 80px;
    }
`;

export const DialogWithIcon = styled.div`
    &.frame-160 {
        display: flex;
        flex-direction: column;
        gap: 16px;
        align-items: center;
        justify-content: flex-start;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
        margin-top: 16px;
        margin-bottom: 16px;
    }
    .help-circle {
        color: var(--text-icons-green-500, #00f260);
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        position: relative;
        overflow: visible;
    }
    .frame-286 {
        padding: 0px 8px 0px 8px;
        display: flex;
        flex-direction: row;
        gap: 0px;
        align-items: flex-start;
        justify-content: center;
        align-self: stretch;
        flex-shrink: 0;
        position: relative;
    }
    .title {
        color: #ffffff;
        text-align: center;
        font-size: 18px;
        font-weight: 500;
        position: relative;
        flex: 1;
    }

    //POPUP
    .info-circle {
        color: var(--text-icons-blue-500);
        flex-shrink: 0;
        width: 40px;
        height: 40px;
        position: relative;
        overflow: visible;
    }
    .custom-popup-text-content {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: center;
        font-size: 14px !important;
        font-weight: 400;
        position: relative;
        align-self: stretch;
    }
    .custom-popup-text-content2 {
        color: var(--text-and-icons-gray-100, #d1d1d1);
        text-align: left;
        font-size: 14px !important;
        font-weight: 400;
        position: relative;
        align-self: stretch;
    }
    .custom-popup-text-content-2-span {
        padding-left: 25px;
    }
`;
// #endregion

// #region GLOBALS
const ENABLE_PAGE_BREAK = true;

const DEFAULT_BRAND_EXPORT_RULES = {
    showProductName: false,
    format: "Capitalized", //Capitalized and Uppercase
    translateDesc: false,
    translateDescLangCode: "", //da, en, no, sv
    translateDescLanguage: "", //Danish, English, Norwegian, Swedish
    addColorName: false,
    productNameColorSource: "Brand", //Brand or POS
    useBrandColorNameInColorCode: false,
    addProductNameSuffix: false,
    productNameSuffix: "", //text appended to product name
    roundPrice: false,
    roundingKey: "", //next0,50, next5, next10, next50, next100, previous-1, previous-50, previous-5
    roundingLabel: "", //Round up to next 0,50, Round up to next 5, etc
    applyCostTariff: false,
    costTariffPercentage: "", //custom tariff percentage applied to cost
    applyDiscount: false,
    costDiscountPercentage: ""
};

const DEFAULT_DESCRIPTIONS_INCLUDED = {
    includeCategory: false,
    includeSpecifications: false,
    includeComposition: false,
    includeWashAndCare: false,
    includeFeatures: false,
    includeQualityDescription: false,
    includeCountryOfOrigin: false
};
// #endregion

export default function POSExport() {
    document.title = `${APP_TITLE} - POS Export`;

    // #region VARS
    const { MainLayoutProps, layoutType } = useOutletContext();
    const { setGenericConfirmationPopup } = useContext(PopupContext);
    const { setHasUnsavedChanges } = useContext(AppContext);
    const [searchParams] = useSearchParams();
    const productID = searchParams.get("productID");
    const productName = searchParams.get("productName");
    const labelBrandID = searchParams.get("labelBrandID");

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [showRulesSaved, setShowRulesSaved] = useState(false);
    const onSaveClickedRef = useRef(null);
    const navigate = useNavigate();
    // #endregion

    // #region STATES
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    const [allLabelBrandsList, setAllLabelBrandsList] = useState([]);
    const [filteredLabelBrand, setFilteredLabelBrand] = useState([]);
    const [requestsEnabled, setRequestsEnabled] = useState(false);

    const [formsEnabled, setFormsEnabled] = useState(false);
    const [saveButtonEnabled, setSaveButtonEnabled] = useState(false);
    const [hideAllCards, setHideAllCards] = useState(true);
    const [showSeasonsTablePopup, setShowSeasonsTablePopup] = useState(false);
    const [showGendersTablePopup, setShowGendersTablePopup] = useState(false);
    const [showCategoriesTablePopup, setShowCategoriesTablePopup] = useState(false);
    const [showColorsTablePopup, setShowColorsTablePopup] = useState(false);
    const [showSizesTablePopup, setShowSizesTablePopup] = useState(false);
    // #endregion

    // #region DATA MODEL STATES
    const [brandExportRules, setBrandExportRules] = useState(DEFAULT_BRAND_EXPORT_RULES);

    const [isProductRandom, setIsProductRandom] = useState(true);
    const [posPageData, setPosPageData] = useState({
        image: undefined,
        name: undefined,
        productNumber: undefined,
        colorCode: undefined,
        brand: undefined,
        productGroup: undefined,
        gender: undefined,
        season: undefined,
        color: undefined,
        costCurrent: undefined,
        salePrice: undefined,
        collections: undefined,
        description: undefined,
        category: undefined,
        specifications: undefined,
        composition: undefined,
        washCare: undefined,
        features: undefined,
        qualityDescription: undefined,
        countryOfOrigin: undefined
    });

    const [identifiers, setIdentifiers] = useState({
        gtins: [], //array of {code, size}
        skus: [] //array of {code, size}
    });

    const [allBrandItems, setAllBrandItems] = useState([]);
    const [allPOSBrands, setAllPOSBrands] = useState([]);
    const [posHasMatch, setPosHasMatch] = useState(undefined);

    const [descriptionsIncluded, setDescriptionsIncluded] = useState(DEFAULT_DESCRIPTIONS_INCLUDED);

    const [posExportRequestData, setPOSExportRequestData] = useState(null);
    const [posSeasons, setPosSeasons] = useState([]);
    const [seasonMappings, setSeasonMappings] = useState([]); //array of {labelBrandName, exportName, seasonIdFromPOS, endOfLifecycleDay, isNOOS}
    const [posGenders, setPosGenders] = useState([]);
    const [genderMappings, setGenderMappings] = useState([]); //array of {originalGenderName, userGenderName}
    const [categoryFieldName, setCategoryFieldName] = useState(null); //"Category" or "Product type"
    // Category rows keep the size-system ID/name normalized from relatedSizeGroupsConvertionTable for the popup UI.
    const [categoryMappings, setCategoryMappings] = useState([]); //array of {labelBrandCategoryName, labelBrandGenderName, exportName, subCategoryExportName}
    const [relatedSizeGroupMappings, setRelatedSizeGroupMappings] = useState([]); //array of {labelBrandCategoryName, labelBrandGenderName, exportName}
    const [categoryMappingsNeedConfirmation, setCategoryMappingsNeedConfirmation] = useState(false);
    const [colorMappings, setColorMappings] = useState([]); //array of {labelBrandName, exportName}
    const [colorMappingsNeedConfirmation, setColorMappingsNeedConfirmation] = useState(false);
    const [posSizesData, setPosSizesData] = useState({});
    const [sizeMappings, setSizeMappings] = useState({});
    const [sizeGroupsConvertionTable, setSizeGroupsConvertionTable] = useState([]);
    const [tempSavedState, setTempSavedState] = useState(null); // temp data + config for no-match scenario
    const hasLocalCategoryMappingsRef = useRef(false);
    const hasLocalColorMappingsRef = useRef(false);
    const colorRequestVersionRef = useRef(0);
    // Server-loaded history survives UI resets; only its exact retail/label owner is protected.
    const savedColorOwnersRef = useRef(new Set());
    const sourceOwnersRef = useRef(new WeakMap());
    const activeSourceOwnerRef = useRef(null);
    const selectedExportOwnerRef = useRef(null);
    selectedExportOwnerRef.current = {
        retailBrandID: filteredRetailBrand?.[0]?.value,
        labelBrandID: filteredLabelBrand?.[0]?.value
    };
    const colorContextKey = JSON.stringify([
        filteredRetailBrand?.[0]?.value,
        filteredLabelBrand?.[0]?.value,
        allPOSBrands.find(brand => brand.selected)?.value
    ]);
    const activeColorContextRef = useRef(colorContextKey);
    activeColorContextRef.current = colorContextKey;

    // #endregion

    // #region SIMPLE UI EVENTS METHODS
    const isSelectedExportOwner = owner =>
        !!owner &&
        typeof owner.retailBrandID === "string" &&
        !!owner.retailBrandID &&
        typeof owner.labelBrandID === "string" &&
        !!owner.labelBrandID &&
        owner.retailBrandID === selectedExportOwnerRef.current.retailBrandID &&
        owner.labelBrandID === selectedExportOwnerRef.current.labelBrandID;

    const isCurrentExportOwner = owner =>
        isSelectedExportOwner(owner) && owner.generation === colorRequestVersionRef.current;

    const invalidateExportSource = () => {
        colorRequestVersionRef.current += 1;
        activeSourceOwnerRef.current = null;
        setTempSavedState(null);
        setPOSExportRequestData(null);
    };

    const applyOwnedExportSource = (data, owner) => {
        if (!isCurrentExportOwner(owner) || !data || typeof data !== "object") return false;
        sourceOwnersRef.current.set(data, owner);
        activeSourceOwnerRef.current = owner;
        setPOSExportRequestData(data);
        return true;
    };

    const onClickRefreshAction = () => {
        getExportProductsConfigurationRequest();
    };

    const onSearch = text => {
        //TODO: implement search later
        console.log("Search text: ", text);
    };

    const onRetailBrandChange = selection => {
        if (selection?.locked) return;
        invalidateExportSource();
        setFilteredRetailBrand([selection]);
    };

    const onLabelBrandChange = selection => {
        if (selection?.locked) return;
        invalidateExportSource();
        setFilteredLabelBrand([selection]);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrand =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrand
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand([defaultRetailBrandObj]);
        }

        if (data?.connectedLabelBrands?.length) {
            const labelBrands = data?.connectedLabelBrands?.map((item, index) => {
                const labelBrandName = item?.name;

                return {
                    name: labelBrandName,
                    value: item?._id
                };
            });

            setAllLabelBrandsList(labelBrands);

            if (labelBrandID) {
                const matchedBrand = labelBrands.find(item => item.value === labelBrandID);
                setFilteredLabelBrand(matchedBrand ? [{ ...matchedBrand, isSelected: true }] : undefined);
            } else setFilteredLabelBrand(undefined);
        }

        setRequestsEnabled(true);
    };

    const onSaveClicked = () => {
        const resolvedBrandName = filteredLabelBrand?.[0]?.name;
        const brandName = resolvedBrandName || "this brand";

        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "JUST CHECKING...",
            headingColor: "var(--text-icons-green-500)",
            title: (
                <DialogWithIcon className="frame-160">
                    <HelpCircle className="help-circle" />
                    <div className="frame-286">
                        <div className="title">Ready to save?</div>
                    </div>
                </DialogWithIcon>
            ),
            messageHtml: `These settings will be saved as default for ${brandName}.`,
            confirmText: "SAVE",
            cancelText: "CANCEL",
            confirmState: "secondary",
            confirmTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                setGenericConfirmationPopup(false);
                updateExportProductsConfiguration();
            },
            onCancelClicked: () => {
                setGenericConfirmationPopup(false);
            }
        });
    };

    const showBrandMatchPopup = ({ posBrandName, botagLabelBrand, onUseMatch, onChooseDifferent, onCreateNew }) => {
        Library.showConfirmationMessage({
            setGenericConfirmationPopup,
            useV2: true,
            headingText: "CONFIRM BRAND MATCH",
            headingColor: "var(--text-icons-green-500)",
            title: `We found “${posBrandName}” in Front Systems. Use it for BOtag brand “${botagLabelBrand}”`,
            messageHtml: "",
            confirmText: "Create new",
            cancelText: "Choose different",
            topButtonText: "Use match",
            showTopButton: true,
            topButtonState: "secondary",
            confirmState: "secondary",
            topButtonTextColor: "var(--text-icons-green-500)",
            onConfirmClicked: () => {
                if (onCreateNew) onCreateNew();
            },
            onCancelClicked: () => {
                if (onChooseDifferent) onChooseDifferent();
            },
            onTopButtonClicked: () => {
                if (onUseMatch) onUseMatch();
            }
        });
    };
    // #endregion

    // #region HELPER METHODS
    const handleGenderMappingsChange = newGenderMappings => {
        setSizeMappings(prevSizeMappings => {
            if (!prevSizeMappings?.bottoms?.inseam?.convertionTable) return prevSizeMappings;

            let updatedConvertionTable = [...prevSizeMappings.bottoms.inseam.convertionTable];
            let hasChanges = false;

            genderMappings.forEach(oldMapping => {
                const newMapping = newGenderMappings.find(m => m.originalGenderName === oldMapping.originalGenderName);
                if (newMapping) {
                    const oldName =
                        oldMapping.userGenderName === "Leave empty / Unknown"
                            ? "Empty / Unknown"
                            : oldMapping.userGenderName;
                    const newName =
                        newMapping.userGenderName === "Leave empty / Unknown"
                            ? "Empty / Unknown"
                            : newMapping.userGenderName;

                    if (oldName && oldName !== newName) {
                        updatedConvertionTable = updatedConvertionTable.map(entry => {
                            if (entry.genderNameOfPOS === oldName) {
                                hasChanges = true;
                                return { ...entry, genderNameOfPOS: newName };
                            }
                            return entry;
                        });
                    }
                }
            });

            if (hasChanges) {
                return {
                    ...prevSizeMappings,
                    bottoms: {
                        ...prevSizeMappings.bottoms,
                        inseam: {
                            ...prevSizeMappings.bottoms.inseam,
                            convertionTable: updatedConvertionTable
                        }
                    }
                };
            }
            return prevSizeMappings;
        });

        setGenderMappings(newGenderMappings);
    };

    const normalizeLifecycleDay = day => {
        if (!day || day === "-") return "";

        const dayValue = String(day);
        if (dayValue.includes("T")) return dayValue.split("T")[0];
        return dayValue;
    };

    const getPOSSeasonByName = (seasonsFromPOS = [], seasonName) => {
        return seasonsFromPOS.find(season => season?.seasonNameFromPOS === seasonName);
    };

    const buildSeasonMappings = (labelBrandSeasons = [], labelBrandSeasonsMatches = [], seasonsFromPOS = []) => {
        return labelBrandSeasons.map(labelBrandSeasonName => {
            const seasonMatch = labelBrandSeasonsMatches.find(match => match?.labelBrandName === labelBrandSeasonName);
            const exportName = seasonMatch?.exportName ?? null;
            const posSeason = getPOSSeasonByName(seasonsFromPOS, exportName);
            const isNOOS = posSeason?.isNOOS === true;

            return {
                labelBrandName: labelBrandSeasonName,
                exportName,
                seasonIdFromPOS: posSeason?.seasonIdFromPOS ?? null,
                endOfLifecycleDay: isNOOS ? "" : normalizeLifecycleDay(posSeason?.endOfLifecycleDay),
                isNOOS
            };
        });
    };

    const hasConversionValue = value => value !== null && value !== undefined && value !== "";

    const buildRelatedSizeGroupMappings = mappings => {
        if (!Array.isArray(mappings)) return [];

        return mappings
            .filter(
                mapping =>
                    mapping?.labelBrandCategoryName &&
                    mapping?.labelBrandGenderName !== null &&
                    mapping?.labelBrandGenderName !== undefined &&
                    hasConversionValue(mapping?.relatedSizeGroupNameFromPOS)
            )
            .map(mapping => ({
                labelBrandCategoryName: mapping.labelBrandCategoryName,
                labelBrandGenderName: mapping.labelBrandGenderName,
                labelBrandSizeGroupSizes: mapping.labelBrandSizeGroupSizes,
                exportName: mapping.relatedSizeGroupNameFromPOS
            }));
    };

    const buildSeasonDefaultsToSave = () => {
        const seasonsFromPOS = posExportRequestData?.seasonsFromPOS || [];
        const noosSeasonIDs = new Set(
            seasonsFromPOS
                .filter(season => season?.isNOOS === true)
                .map(season => season?.seasonIdFromPOS)
                .filter(seasonID => seasonID !== null && seasonID !== undefined)
        );

        const originalLifecycleByPOSSeasonID = new Map(
            seasonsFromPOS
                .filter(season => season?.seasonIdFromPOS !== null && season?.seasonIdFromPOS !== undefined)
                .map(season => [season.seasonIdFromPOS, normalizeLifecycleDay(season?.endOfLifecycleDay)])
        );

        const changedDefaultsByPOSSeasonID = new Map();

        seasonMappings.forEach(mapping => {
            if (mapping?.seasonIdFromPOS === null || mapping?.seasonIdFromPOS === undefined) return;
            if (noosSeasonIDs.has(mapping.seasonIdFromPOS)) return;

            const nextLifecycleDay = normalizeLifecycleDay(mapping?.endOfLifecycleDay);
            const originalLifecycleDay = originalLifecycleByPOSSeasonID.get(mapping.seasonIdFromPOS) ?? "";

            if (nextLifecycleDay === originalLifecycleDay) return;

            changedDefaultsByPOSSeasonID.set(mapping.seasonIdFromPOS, {
                seasonIdFromPOS: mapping.seasonIdFromPOS,
                endOfLifecycleDay: nextLifecycleDay || null
            });
        });

        return Array.from(changedDefaultsByPOSSeasonID.values());
    };

    const syncSavedSeasonDefaults = seasonDefaults => {
        if (!seasonDefaults?.length) return;

        const savedLifecycleByPOSSeasonID = new Map(
            seasonDefaults.map(season => [season.seasonIdFromPOS, season.endOfLifecycleDay ?? ""])
        );

        setPOSExportRequestData(prev => {
            if (!prev?.seasonsFromPOS?.length) return prev;

            const next = {
                ...prev,
                seasonsFromPOS: prev.seasonsFromPOS.map(season => {
                    if (!savedLifecycleByPOSSeasonID.has(season?.seasonIdFromPOS)) return season;

                    return {
                        ...season,
                        endOfLifecycleDay: savedLifecycleByPOSSeasonID.get(season.seasonIdFromPOS)
                    };
                })
            };
            const owner = sourceOwnersRef.current.get(prev);
            if (owner) sourceOwnersRef.current.set(next, owner);
            return next;
        });
    };

    const resetExportConfigurationState = ({
        categoryMappingsNeedConfirmation: nextCategoryMappingsNeedConfirmation = false,
        colorMappings: nextColorMappings = [],
        colorMappingsNeedConfirmation: nextColorMappingsNeedConfirmation = false,
        resetColorEdits = false
    } = {}) => {
        hasLocalCategoryMappingsRef.current = false;
        if (resetColorEdits) hasLocalColorMappingsRef.current = false;

        setBrandExportRules(DEFAULT_BRAND_EXPORT_RULES);
        setDescriptionsIncluded(DEFAULT_DESCRIPTIONS_INCLUDED);
        setGenderMappings([]);
        setCategoryFieldName(null);
        setCategoryMappings([]);
        setRelatedSizeGroupMappings([]);
        setCategoryMappingsNeedConfirmation(nextCategoryMappingsNeedConfirmation);
        applyColorMappings(nextColorMappings, { needConfirmation: nextColorMappingsNeedConfirmation });
        setSizeMappings({});
        setSizeGroupsConvertionTable([]);
    };

    const applyCategoryConfiguration = ({
        nextCategoryFieldName = null,
        nextCategoryMappings = [],
        nextRelatedSizeGroupMappings = [],
        needConfirmation = false
    } = {}) => {
        if (hasLocalCategoryMappingsRef.current) return;

        setCategoryFieldName(nextCategoryFieldName);
        setCategoryMappings(nextCategoryMappings);
        setRelatedSizeGroupMappings(nextRelatedSizeGroupMappings);
        setCategoryMappingsNeedConfirmation(needConfirmation);
    };

    const handleCategoryMappingsChange = nextCategoryMappings => {
        hasLocalCategoryMappingsRef.current = true;
        setCategoryMappings(nextCategoryMappings);
        setRelatedSizeGroupMappings(buildRelatedSizeGroupMappings(nextCategoryMappings));
        setCategoryMappingsNeedConfirmation(false);
    };

    const applyColorMappings = (nextColorMappings, { needConfirmation = false } = {}) => {
        if (hasLocalColorMappingsRef.current) return;

        setColorMappings(nextColorMappings);
        setColorMappingsNeedConfirmation(needConfirmation);
    };

    const handleColorMappingsChange = (nextColorMappings, expectedContext = colorContextKey) => {
        if (activeColorContextRef.current !== expectedContext) return;
        hasLocalColorMappingsRef.current = true;
        setColorMappings(nextColorMappings);
        setColorMappingsNeedConfirmation(false);
    };

    const applySavedConfiguration = (
        config,
        genders = [],
        isDefaultConfiguration,
        isLabelBrandConfig,
        initialColorMappings = [],
        sizeGroupsFromPOS = [],
        hasCategoryAIMatches = false,
        colorResponse = posExportRequestData
    ) => {
        // Translate language
        const hasTranslation = !!config.translateLanguageName;
        const matchedLang = LANGUAGES_OPTIONS.find(l => l.name === config.translateLanguageName);

        // Rounding
        const hasRounding = !!config.pricesRoundUp;
        const matchedRounding = ROUNDING_PRICE_OPTIONS.find(r => r.value === config.pricesRoundUp);

        // Discount
        const hasDiscount = config.costDiscountPercentage !== undefined && config.costDiscountPercentage !== null;

        // Product name suffix
        const hasSuffix = !!config.productNameSuffix;

        // Custom tariff
        const hasTariff = config.costTariffPercentage !== undefined && config.costTariffPercentage !== null;

        const addBrandColorName = !!config.productNameWithColorName;
        const addPOSColorName = !!config.productNameWithPosColorName;

        setBrandExportRules({
            showProductName: config.productNameAsCAPS || addBrandColorName || addPOSColorName || false,
            format: config.productNameAsCAPS ? "Uppercase" : "Capitalized",
            addColorName: addBrandColorName || addPOSColorName,
            productNameColorSource: addPOSColorName ? "POS" : "Brand",
            useBrandColorNameInColorCode: !!config.useBrandColorNameInColorCode,
            addProductNameSuffix: hasSuffix,
            productNameSuffix: config.productNameSuffix || "",
            translateDesc: hasTranslation,
            translateDescLangCode: matchedLang?.value || "",
            translateDescLanguage: matchedLang?.name || config.translateLanguageName || "",
            roundPrice: hasRounding,
            roundingKey: hasRounding ? config.pricesRoundUp : "",
            roundingLabel: matchedRounding?.name || "",
            applyCostTariff: hasTariff,
            costTariffPercentage: hasTariff ? `${String(config.costTariffPercentage).replace(".", ",")}%` : "",
            applyDiscount: hasDiscount,
            costDiscountPercentage: hasDiscount ? `${String(config.costDiscountPercentage).replace(".", ",")}%` : ""
        });

        setGenderMappings(
            config.gendersConvertionTable?.length
                ? config.gendersConvertionTable
                      .map(s => ({
                          originalGenderName: s.labelBrandName,
                          userGenderName: s.exportName === "" ? "Leave empty / Unknown" : s.exportName
                      }))
                      .filter(s => genders.some(sb => sourceValuesMatch(sb.name, s.originalGenderName)))
                : []
        );

        setSizeMappings(config.sizesConvertionTable || {});
        setSizeGroupsConvertionTable(
            isLabelBrandConfig && Array.isArray(config.sizeGroupsConvertionTable)
                ? config.sizeGroupsConvertionTable
                : []
        );

        const savedCategoryMappings = Array.isArray(config.categoriesConvertionTable)
            ? config.categoriesConvertionTable
            : [];
        const savedSubCategoryMappings = Array.isArray(config.subCategoriesConvertionTable)
            ? config.subCategoriesConvertionTable
            : [];
        const savedRelatedSizeGroupMappings =
            isLabelBrandConfig && Array.isArray(config.relatedSizeGroupsConvertionTable)
                ? config.relatedSizeGroupsConvertionTable.map(mapping => ({
                      labelBrandCategoryName: mapping?.labelBrandCategoryName,
                      labelBrandGenderName: mapping?.labelBrandGenderName,
                      labelBrandSizeGroupSizes: mapping?.labelBrandSizeGroupSizes,
                      exportName: mapping?.exportName
                  }))
                : [];
        const availableSizeGroups = Array.isArray(sizeGroupsFromPOS) ? sizeGroupsFromPOS : [];
        const hasSubCategoryMappings = Array.isArray(config.subCategoriesConvertionTable);
        const nextCategoryMappings = savedCategoryMappings.map(mapping => {
            const subCategoryMapping = findCategoryMapping(
                savedSubCategoryMappings,
                mapping.labelBrandCategoryName,
                mapping.labelBrandGenderName,
                mapping.labelBrandSizeGroupSizes
            );
            const relatedSizeGroupMapping = findCategoryMapping(
                savedRelatedSizeGroupMappings,
                mapping.labelBrandCategoryName,
                mapping.labelBrandGenderName,
                mapping.labelBrandSizeGroupSizes
            );
            const relatedSizeGroupName = relatedSizeGroupMapping?.exportName;
            const hasRelatedSizeGroupMapping =
                relatedSizeGroupName !== null && relatedSizeGroupName !== undefined && relatedSizeGroupName !== "";
            const relatedSizeGroup = hasRelatedSizeGroupMapping
                ? availableSizeGroups.find(sizeGroup => sizeGroup?.sizeGroupName === relatedSizeGroupName)
                : null;

            return {
                labelBrandCategoryName: mapping.labelBrandCategoryName,
                labelBrandGenderName: mapping.labelBrandGenderName,
                labelBrandSizeGroupSizes: mapping.labelBrandSizeGroupSizes,
                exportName: mapping.exportName,
                ...(hasSubCategoryMappings ? { subCategoryExportName: subCategoryMapping?.exportName ?? "" } : {}),
                ...(hasRelatedSizeGroupMapping
                    ? {
                          relatedSizeGroupIdFromPOS: relatedSizeGroup?.sizeGroupID ?? null,
                          relatedSizeGroupNameFromPOS: relatedSizeGroupName
                      }
                    : {})
            };
        });
        applyCategoryConfiguration({
            nextCategoryFieldName: isLabelBrandConfig && config.categoryFieldName ? config.categoryFieldName : null,
            nextCategoryMappings: isLabelBrandConfig ? nextCategoryMappings : [],
            nextRelatedSizeGroupMappings: isLabelBrandConfig ? savedRelatedSizeGroupMappings : [],
            needConfirmation: !isLabelBrandConfig && hasCategoryAIMatches
        });
        const savedColorMappings = Array.isArray(config.colorsConvertionTable) ? config.colorsConvertionTable : [];
        const colorState = mergeColorMappings(
            colorResponse?.labelBrandColors,
            isLabelBrandConfig ? savedColorMappings : [],
            initialColorMappings,
            colorResponse?.colorsFromPOS
        );
        applyColorMappings(colorState.mappings, { needConfirmation: colorState.needConfirmation });

        // Description options
        if (config.descriptionOptions) {
            setDescriptionsIncluded({
                includeCategory: config.descriptionOptions.includeCategory || false,
                includeSpecifications: config.descriptionOptions.includeSpecifications || false,
                includeComposition: config.descriptionOptions.includeComposition || false,
                includeWashAndCare: config.descriptionOptions.includeWashAndCare || false,
                includeFeatures: config.descriptionOptions.includeFeatures || false,
                includeQualityDescription: config.descriptionOptions.includeQualityDescription || false,
                includeCountryOfOrigin: config.descriptionOptions.includeCountryOfOrigin || false
            });
        }

        if (isDefaultConfiguration === false) {
            Library.showToastMessage({
                title: "Configuration loaded!",
                message: "Previously saved export settings have been loaded.",
                type: "success"
            });
        }

        setSaveButtonEnabled(false);
    };

    const onManualBrandMatch = () => {
        if (
            tempSavedState &&
            (!isCurrentExportOwner(tempSavedState.owner) ||
                sourceOwnersRef.current.get(tempSavedState.posExportRequestData) !== tempSavedState.owner)
        ) {
            setTempSavedState(null);
            return;
        }
        // Enable forms and save button on manual brand selection
        setFormsEnabled(true);
        setSaveButtonEnabled(true);

        // Load temp saved state if exists (populate pre-mapped values and config into UI states)
        if (tempSavedState) {
            const {
                brandItems,
                identifiers: savedIdentifiers,
                posData,
                seasons,
                genders,
                labelBrandGenders,
                labelBrandSizes,
                seasonMappings: savedSeasonMappings,
                categoryMappingsNeedConfirmation: savedCategoryMappingsNeedConfirmation,
                colorMappings: savedColorMappings,
                posExportRequestData: savedPosExportRequestData,
                savedConfig,
                isDefaultConfiguration,
                isLabelBrandConfig
            } = tempSavedState;

            setAllBrandItems(brandItems);
            setIdentifiers(savedIdentifiers);
            setPosPageData(posData);
            setIsProductRandom(true);
            setPosSeasons(seasons);
            setPosGenders(genders);
            setPosSizesData(labelBrandSizes);
            setSeasonMappings(savedSeasonMappings || []);
            if (!applyOwnedExportSource(savedPosExportRequestData, tempSavedState.owner)) return;

            if (savedConfig)
                applySavedConfiguration(
                    savedConfig,
                    labelBrandGenders,
                    isDefaultConfiguration,
                    isLabelBrandConfig,
                    savedColorMappings || [],
                    savedPosExportRequestData?.sizeGroupsFromPOS || [],
                    !!savedCategoryMappingsNeedConfirmation,
                    savedPosExportRequestData
                );
            else
                resetExportConfigurationState({
                    categoryMappingsNeedConfirmation: !!savedCategoryMappingsNeedConfirmation,
                    colorMappings: savedColorMappings || [],
                    colorMappingsNeedConfirmation: !!savedColorMappings?.length
                });

            setTempSavedState(null);
        }
    };

    const resetDataStates = ({ resetColorEdits = false } = {}) => {
        setSaveButtonEnabled(false);
        setFormsEnabled(false);

        // Reset all data states to defaults
        setAllPOSBrands(prev => prev.map(item => ({ ...item, selected: false })));

        setAllBrandItems([]);
        setPosHasMatch(undefined);
        setPosSeasons([]);
        setPosGenders([]);
        setPosPageData({
            image: undefined,
            name: undefined,
            productNumber: undefined,
            colorCode: undefined,
            brand: undefined,
            productGroup: undefined,
            gender: undefined,
            season: undefined,
            color: undefined,
            costCurrent: undefined,
            salePrice: undefined,
            collections: undefined,
            description: undefined,
            category: undefined,
            specifications: undefined,
            composition: undefined,
            washCare: undefined,
            features: undefined,
            qualityDescription: undefined,
            countryOfOrigin: undefined
        });
        setIdentifiers({ gtins: [], skus: [] });
        resetExportConfigurationState({ resetColorEdits });
        setSeasonMappings([]);
        setPosSizesData({});
        setIsProductRandom(true);
        setPOSExportRequestData(null);
    };
    // #endregion

    // #region REQUESTS
    const getExportProductsConfigurationRequest = async () => {
        const request = {
            retailBrandID: filteredRetailBrand?.[0]?.value,
            labelBrandID: filteredLabelBrand?.[0]?.value
        };
        if (!isSelectedExportOwner(request)) return;
        invalidateExportSource();
        const colorRequestVersion = colorRequestVersionRef.current;
        const sourceOwner = { ...request, generation: colorRequestVersion };
        hasLocalCategoryMappingsRef.current = false;

        const res = await Library.makeApiRequest({
            endpoint: "getExportProductsConfiguration",
            method: "POST",
            data: request,
            setLoader: setIsLoading
        });

        if (!isCurrentExportOwner(sourceOwner) || res?.data?.status !== 200) return;

        const {
            needSetupOfPOS = false,
            labelBrandProducts = [],
            randomProductData = {},
            posLabelBrands = [],
            matchingPosLabelBrandID = undefined,
            exactMatch = true,
            labelBrandSeasons = [], // String array
            labelBrandGenders = [], // Object array {name: string, value: string}
            labelBrandSizes = {},
            labelBrandColors = [],
            labelBrandColorsMatches = [],
            labelBrandCategoriesMatches = [],
            labelBrandProductTypesMatches = [],
            seasonsFromPOS = [],
            labelBrandSeasonsMatches = [],
            sizeGroupsFromPOS = [],
            exportProductsConfiguration = {}
        } = res?.data?.data;

        const rawPosExportRequestData = res?.data?.data;
        if (!applyOwnedExportSource(rawPosExportRequestData, sourceOwner)) return;

        if (needSetupOfPOS) {
            setHideAllCards(true);

            Library.showConfirmationMessage({
                setGenericConfirmationPopup,
                useV2: true,
                headingText: "ATTENTION!",
                headingColor: "var(--text-icons-orange-500)",
                title: "POS setup needed!",
                messageHtml:
                    "The connection between BOtag and your POS system is not yet configured. A POS connection is required to config the export settings.",
                confirmText: "POS Setup",
                cancelText: "CANCEL",
                confirmState: "secondary",
                onConfirmClicked: () => {
                    setGenericConfirmationPopup(false);
                    navigate("/stores/pos");
                },
                onCancelClicked: () => {
                    setGenericConfirmationPopup(false);
                }
            });

            return;
        }

        setHideAllCards(false);

        // --- Pre-map all data from API response ---
        const brandItems = labelBrandProducts?.map(item => ({
            value: item?._id,
            name: item?.name,
            selected: randomProductData?._id === item?._id
        }));

        const gtins = randomProductData?.gtins?.map(item => ({
            code: item?.barcode,
            size: item?.size
        }));
        const skus = randomProductData?.gtins?.map(item => ({
            code: item?.SKU || item?.sku || "-",
            size: item?.size
        }));

        const posData = {
            image: randomProductData?.productImages?.[0],
            name: randomProductData?.name,
            productNumber: randomProductData?.referenceCode,
            colorCode: randomProductData?.gtins?.[0]?.colorCode,
            brand: randomProductData?.labelBrandData?.name,
            productGroup: randomProductData?.productType,
            sizes: randomProductData?.gtins?.map(item => item?.size) || [],
            gender: randomProductData?.gender,
            season: randomProductData?.season,
            color: randomProductData?.gtins?.[0]?.colorName,
            costCurrent: randomProductData?.priceIN, //CONVERT to number
            salePrice: randomProductData?.priceOUT, //CONVERT to number
            collections: "",
            description: randomProductData?.description,
            category: randomProductData?.category,
            specifications: randomProductData?.specifications,
            composition: randomProductData?.composition,
            washCare: randomProductData?.washAndCare,
            features: randomProductData?.features,
            qualityDescription: randomProductData?.qualityDescription,
            countryOfOrigin: randomProductData?.countryOfOrigin
        };

        const seasons = labelBrandSeasons?.map(item => ({
            value: item,
            name: item,
            selected: false
        }));

        const initialSeasonMappings = buildSeasonMappings(labelBrandSeasons, labelBrandSeasonsMatches, seasonsFromPOS);
        const initialColorMappings = buildColorSuggestions(
            labelBrandColors,
            labelBrandColorsMatches,
            rawPosExportRequestData?.colorsFromPOS
        );
        const hasCategoryAIMatches =
            (Array.isArray(labelBrandCategoriesMatches) && labelBrandCategoriesMatches.length > 0) ||
            (Array.isArray(labelBrandProductTypesMatches) && labelBrandProductTypesMatches.length > 0);

        const genders = labelBrandGenders?.map(item => ({
            value: item?.name ?? "Not provided",
            name: item?.name ?? "Not provided",
            total: item?.total,
            selected: false
        }));

        //manually add gender Not provided
        /*genders.push({
            value: "Not provided",
            name: "Not provided",
            total: 10,
            selected: false
        });*/

        // Saved configuration data
        const savedConfig = exportProductsConfiguration?.configuration;
        const isDefaultConfiguration = exportProductsConfiguration?.isDefaultConfiguration;

        // --- POS Brand matching ---
        const isLabelBrandConfig = filteredLabelBrand?.[0]?.value === exportProductsConfiguration?.labelBrandID;
        if (
            isLabelBrandConfig &&
            Array.isArray(savedConfig?.colorsConvertionTable) &&
            savedConfig.colorsConvertionTable.some(
                mapping => typeof mapping?.exportName === "string" && mapping.exportName.trim()
            )
        ) {
            savedColorOwnersRef.current.add(JSON.stringify([request.retailBrandID, request.labelBrandID]));
        }

        // Helper to populate all UI states when a match is accepted
        const applyMatchedState = posList => {
            if (!isCurrentExportOwner(sourceOwner)) return;
            setTempSavedState(null);
            setAllPOSBrands(posList);
            setPosHasMatch(true);
            setSaveButtonEnabled(true);
            setFormsEnabled(true);
            setAllBrandItems(brandItems);
            setIdentifiers({ gtins, skus });
            setPosPageData(posData);
            setIsProductRandom(true);
            setPosSeasons(seasons);
            setPosGenders(genders);
            setPosSizesData(labelBrandSizes);
            setSeasonMappings(initialSeasonMappings);
            applyOwnedExportSource(rawPosExportRequestData, sourceOwner);

            if (savedConfig)
                applySavedConfiguration(
                    savedConfig,
                    labelBrandGenders,
                    isDefaultConfiguration,
                    isLabelBrandConfig,
                    initialColorMappings,
                    sizeGroupsFromPOS,
                    hasCategoryAIMatches,
                    rawPosExportRequestData
                );
            else
                resetExportConfigurationState({
                    categoryMappingsNeedConfirmation: hasCategoryAIMatches,
                    colorMappings: initialColorMappings,
                    colorMappingsNeedConfirmation: initialColorMappings.length > 0
                });
        };

        // Helper to cache data for later manual selection
        const cacheTempState = () => {
            if (!isCurrentExportOwner(sourceOwner)) return;
            setTempSavedState({
                owner: sourceOwner,
                brandItems,
                identifiers: { gtins, skus },
                posData,
                seasons,
                genders,
                labelBrandGenders,
                labelBrandSizes,
                seasonMappings: initialSeasonMappings,
                categoryMappingsNeedConfirmation: hasCategoryAIMatches,
                colorMappings: initialColorMappings,
                posExportRequestData: rawPosExportRequestData,
                savedConfig: savedConfig || null,
                isDefaultConfiguration,
                isLabelBrandConfig
            });
        };

        const posBrandsList = [
            {
                value: "create-new",
                name: `Create new: ${filteredLabelBrand?.[0]?.name}`,
                selected: isLabelBrandConfig && savedConfig?.posLabelBrandName === ""
            },
            ...posLabelBrands?.map(item => ({
                value: item?.id,
                name: item?.name,
                selected: isLabelBrandConfig
                    ? savedConfig?.posLabelBrandName === ""
                        ? false
                        : item?.name === savedConfig?.posLabelBrandName
                    : item?.id === matchingPosLabelBrandID
            }))
        ];

        if (!isLabelBrandConfig) {
            if (matchingPosLabelBrandID) {
                if (exactMatch) {
                    // --- Exact match ---

                    Library.showToastMessage({
                        title: "Brand name match!",
                        message:
                            "We found the same Brand in your POS system. If this match isn't correct, select the correct one from the dropdown.",
                        type: "success"
                    });
                } else {
                    // --- Inexact match: show popup to let user decide ---

                    // Reset UI to clean state while user decides
                    const unselectedList = posBrandsList.map(item => ({ ...item, selected: false }));
                    setAllPOSBrands(unselectedList);
                    setPosHasMatch(undefined);
                    resetDataStates();
                    cacheTempState();

                    const matchedBrandName = posLabelBrands?.find(b => b?.id === matchingPosLabelBrandID)?.name;

                    showBrandMatchPopup({
                        posBrandName: matchedBrandName,
                        botagLabelBrand: filteredLabelBrand?.[0]?.name,
                        onUseMatch: () => {
                            if (!isCurrentExportOwner(sourceOwner)) return;
                            // Re-select the matched brand and apply all data
                            const selectedList = posBrandsList.map(item => ({
                                ...item,
                                selected: item.value === matchingPosLabelBrandID
                            }));
                            applyMatchedState(selectedList);
                            setTempSavedState(null);

                            if (!savedConfig) {
                                Library.showToastMessage({
                                    title: "Brand name match!",
                                    message:
                                        "We found the same Brand in your POS system. If this match isn't correct, select the correct one from the dropdown.",
                                    type: "success"
                                });
                            }
                        },
                        onChooseDifferent: () => {
                            if (!isCurrentExportOwner(sourceOwner)) return;
                            // Already reset above — just leave dropdown unselected
                            setPosHasMatch(false);
                        },
                        onCreateNew: () => {
                            if (!isCurrentExportOwner(sourceOwner)) return;
                            // Select "I don't have this brand (create new)" and populate data directly
                            // (can't use onManualBrandMatch — tempSavedState hasn't flushed yet, stale closure)
                            const createNewList = unselectedList.map(item => ({
                                ...item,
                                selected: item.value === "create-new"
                            }));
                            setAllPOSBrands(createNewList);
                            setFormsEnabled(true);
                            setSaveButtonEnabled(true);
                            setAllBrandItems(brandItems);
                            setIdentifiers({ gtins, skus });
                            setPosPageData(posData);
                            setIsProductRandom(true);
                            setPosSeasons(seasons);
                            setPosGenders(genders);
                            setPosSizesData(labelBrandSizes);
                            setSeasonMappings(initialSeasonMappings);
                            applyOwnedExportSource(rawPosExportRequestData, sourceOwner);

                            if (savedConfig)
                                applySavedConfiguration(
                                    savedConfig,
                                    labelBrandGenders,
                                    isDefaultConfiguration,
                                    isLabelBrandConfig,
                                    initialColorMappings,
                                    sizeGroupsFromPOS,
                                    hasCategoryAIMatches,
                                    rawPosExportRequestData
                                );
                            else
                                resetExportConfigurationState({
                                    categoryMappingsNeedConfirmation: hasCategoryAIMatches,
                                    colorMappings: initialColorMappings,
                                    colorMappingsNeedConfirmation: initialColorMappings.length > 0
                                });
                            setTempSavedState(null);
                        }
                    });

                    return;
                }
            } else {
                // --- No match at all ---

                setAllPOSBrands(posBrandsList);
                resetDataStates();
                cacheTempState();
                setPosHasMatch(false);

                Library.showToastMessage({
                    title: "No match found!",
                    message: "We could not find this brand in your POS. Please select from Brand Name dropdown.",
                    type: "warning"
                });

                return;
            }
        }

        applyMatchedState(posBrandsList);
    };

    const getProductDataForPosExportRequest = async productID => {
        const request = { productID: productID };
        setIsProductRandom(false);

        const res = await Library.makeApiRequest({
            endpoint: "getProductDataForPosExport",
            method: "POST",
            data: request,
            setLoader: setIsLoading
        });

        if (res?.data?.status !== 200) return;

        const productData = res?.data?.data;

        const posData = {
            image: productData?.productImages?.[0],
            name: productData?.name,
            productNumber: productData?.referenceCode,
            colorCode: productData?.gtins?.[0]?.colorCode,
            brand: productData?.labelBrandData?.name,
            productGroup: productData?.productType,
            sizes: productData?.gtins?.map(item => item?.size) || [],
            gender: productData?.gender,
            season: productData?.season,
            color: productData?.gtins?.[0]?.colorName,
            costCurrent: productData?.priceIN, //CONVERT to number
            salePrice: productData?.priceOUT, //CONVERT to number
            collections: "",
            description: productData?.description,
            category: productData?.category,
            specifications: productData?.specifications,
            composition: productData?.composition,
            washCare: productData?.washAndCare,
            features: productData?.features,
            qualityDescription: productData?.qualityDescription,
            countryOfOrigin: productData?.countryOfOrigin
        };

        setPosPageData(posData);

        const gtins = productData?.gtins?.map(item => ({
            code: item?.barcode,
            size: item?.size
        }));
        const skus = productData?.gtins?.map(item => ({
            code: item?.SKU || item?.sku || "-",
            size: item?.size
        }));
        setIdentifiers({ gtins, skus });

        setAllBrandItems(prev =>
            prev.map(item => ({
                ...item,
                selected: productID === item.value
            }))
        );
    };

    const updateExportProductsConfiguration = async () => {
        const sourceOwner = posExportRequestData
            ? sourceOwnersRef.current.get(posExportRequestData)
            : activeSourceOwnerRef.current;
        if (!isCurrentExportOwner(sourceOwner)) {
            Library.showToastMessage({
                title: "Export settings need refreshing",
                message: "Refresh this brand's export settings before saving.",
                type: "warning"
            });
            return;
        }
        const selectedPOSBrand = allPOSBrands?.find(b => b.selected);
        const resolvedBrandName = selectedPOSBrand?.name?.toLowerCase()?.startsWith("create new: ")
            ? ""
            : selectedPOSBrand?.name;

        if (categoryMappingsNeedConfirmation) {
            Library.showToastMessage({
                title: "Category mappings need confirmation",
                message:
                    "Open Customize Categories and save the AI-suggested category matches before saving export settings.",
                type: "warning"
            });
            return;
        }

        if (hasLabelBrandProducts && !categoryMappingsComplete) {
            Library.showToastMessage({
                title: "Category mappings need attention",
                message:
                    "Open Customize Categories and complete the category and POS Size System for every brand size group before saving.",
                type: "warning"
            });
            return;
        }

        if (savedSizeMappingConflicts.size > 0) {
            Library.showToastMessage({
                title: "Size mappings need attention",
                message:
                    "Different sizes on the same brand's product share a POS size. Resolve the conflicts in Customize Sizes before saving.",
                type: "warning"
            });
            return;
        }

        const hasInvalidColorMappings =
            posExportRequestData?.labelBrandColors?.length > 0 &&
            !areColorsMapped(posExportRequestData.labelBrandColors, colorMappings, posExportRequestData.colorsFromPOS);
        const hasKnownSavedColors = savedColorOwnersRef.current.has(
            JSON.stringify([filteredRetailBrand?.[0]?.value, filteredLabelBrand?.[0]?.value])
        );
        const hasUnavailableColorSource =
            !posExportRequestData?.labelBrandColors?.length && (colorMappings.length > 0 || hasKnownSavedColors);
        if (colorMappingsNeedConfirmation || hasInvalidColorMappings || hasUnavailableColorSource) {
            Library.showToastMessage({
                title: "Color mappings need confirmation",
                message: "Open Customize Colors, review the mappings and save them before saving export settings.",
                type: "warning"
            });
            return;
        }

        //if "Leave empty / Unknown" then send as empty string
        //also filter out any uderGenderName undefined
        const genderMappingsToSave = genderMappings
            .filter(m => m.userGenderName !== undefined)
            .map(m => ({
                labelBrandName: m.originalGenderName,
                exportName: m.userGenderName === "Leave empty / Unknown" ? "" : m.userGenderName
            }));

        const addBrandColorName = brandExportRules.addColorName && brandExportRules.productNameColorSource !== "POS";
        const addPOSColorName = brandExportRules.addColorName && brandExportRules.productNameColorSource === "POS";
        // Expand legacy rules over the current size families even when saving without reopening the popup.
        const categoryMappingsWithSizes = expandCategoryMappingsForSizeGroups(categoryMappings, categorySourceRows);
        const categoryMappingsToSave = categoryMappingsWithSizes.filter(
            mapping =>
                mapping.labelBrandCategoryName &&
                mapping.labelBrandGenderName !== null &&
                mapping.labelBrandGenderName !== undefined &&
                mapping.exportName !== null &&
                mapping.exportName !== undefined
        );
        const categoryMappingKeysToSave = new Set(categoryMappingsToSave.map(getCategoryMappingKey));
        const relatedMappingsWithSizes = expandCategoryMappingsForSizeGroups(
            relatedSizeGroupMappings,
            categorySourceRows
        );
        const relatedSizeGroupMappingsToSave = relatedMappingsWithSizes.filter(
            mapping =>
                categoryMappingKeysToSave.has(getCategoryMappingKey(mapping)) && hasConversionValue(mapping?.exportName)
        );

        const exportProductsConfiguration = {
            posLabelBrandName: resolvedBrandName || "",
            productNameAsCAPS: brandExportRules.format === "Uppercase",
            productNameWithColorName: addBrandColorName,
            productNameWithPosColorName: addPOSColorName,
            useBrandColorNameInColorCode: !!brandExportRules.useBrandColorNameInColorCode,
            productNameSuffix: brandExportRules.addProductNameSuffix ? brandExportRules.productNameSuffix : "",
            translateLanguageName: brandExportRules.translateDesc ? brandExportRules.translateDescLanguage : "",
            pricesRoundUp: brandExportRules.roundPrice ? brandExportRules.roundingKey : "",
            costTariffPercentage:
                brandExportRules.applyCostTariff && brandExportRules.costTariffPercentage
                    ? Number(brandExportRules.costTariffPercentage.replace("%", "").replace(",", "."))
                    : null,
            costDiscountPercentage:
                brandExportRules.applyDiscount && brandExportRules.costDiscountPercentage
                    ? Number(brandExportRules.costDiscountPercentage.replace("%", "").replace(",", "."))
                    : null,
            seasonsConvertionTable: seasonMappings
                .map(m => ({
                    labelBrandName: m.labelBrandName,
                    exportName: m.exportName
                }))
                .filter(m => m.exportName !== null && m.exportName !== undefined),
            gendersConvertionTable: genderMappingsToSave,
            categoryFieldName: categoryFieldName || "",
            categoriesConvertionTable: categoryMappingsToSave.map(mapping => ({
                labelBrandCategoryName: mapping.labelBrandCategoryName,
                labelBrandGenderName: mapping.labelBrandGenderName,
                labelBrandSizeGroupSizes: mapping.labelBrandSizeGroupSizes,
                exportName: mapping.exportName
            })),
            subCategoriesConvertionTable: categoryMappingsToSave.map(mapping => ({
                labelBrandCategoryName: mapping.labelBrandCategoryName,
                labelBrandGenderName: mapping.labelBrandGenderName,
                labelBrandSizeGroupSizes: mapping.labelBrandSizeGroupSizes,
                exportName: mapping.subCategoryExportName ?? ""
            })),
            relatedSizeGroupsConvertionTable: relatedSizeGroupMappingsToSave.map(mapping => ({
                labelBrandCategoryName: mapping.labelBrandCategoryName,
                labelBrandGenderName: mapping.labelBrandGenderName,
                labelBrandSizeGroupSizes: mapping.labelBrandSizeGroupSizes,
                exportName: mapping.exportName
            })),
            colorsConvertionTable: colorMappings
                .map(m => ({
                    labelBrandName: m.labelBrandName,
                    exportName: m.exportName
                }))
                .filter(m => m.exportName !== null && m.exportName !== undefined),
            sizesConvertionTable: sizeMappings,
            sizeGroupsConvertionTable,
            descriptionOptions: { ...descriptionsIncluded }
        };

        const request = {
            retailBrandID: filteredRetailBrand?.[0]?.value,
            labelBrandID: filteredLabelBrand?.[0]?.value,
            exportProductsConfiguration: exportProductsConfiguration
        };

        const seasonDefaults = buildSeasonDefaultsToSave();

        const res = await Library.makeApiRequest({
            endpoint: "updateExportProductsConfiguration",
            method: "POST",
            data: request,
            setLoader: setIsSaving
        });

        if (res?.data?.status !== 200) return;

        if (seasonDefaults.length > 0) {
            const seasonDefaultsRes = await Library.makeApiRequest({
                endpoint: "updateSeasonDefaults",
                method: "POST",
                data: {
                    retailBrandID: filteredRetailBrand?.[0]?.value,
                    seasonDefaults
                },
                setLoader: setIsSaving
            });

            if (seasonDefaultsRes?.data?.status !== 200) return;

            syncSavedSeasonDefaults(seasonDefaults);
        }

        Library.showToastMessage({
            title: "POS export setting saved!",
            message: `Settings saved for ${filteredLabelBrand?.[0]?.name}.`,
            type: "success"
        });

        hasLocalCategoryMappingsRef.current = false;
        // Keep confirmed local colours protected from an older in-flight refresh.
        setCategoryMappingsNeedConfirmation(false);
        setHasUnsavedChanges(false);
        setSaveButtonEnabled(false);
    };
    // #endregion

    // #region EFFECTS
    onSaveClickedRef.current = onSaveClicked; // Keep the ref always pointing to the latest onSaveClicked

    useEffect(() => {
        setupHeaders(
            props,
            onClickRefreshAction,
            onSearch,
            onFiltersLoaded,
            allRetailBrandsList,
            onRetailBrandChange,
            filteredRetailBrand,
            allLabelBrandsList,
            onLabelBrandChange,
            filteredLabelBrand,
            saveButtonEnabled,
            () => onSaveClickedRef.current?.(),
            productID,
            productName
        );
    }, [
        allRetailBrandsList,
        filteredRetailBrand,
        allLabelBrandsList,
        filteredLabelBrand,
        saveButtonEnabled,
        productID,
        productName
    ]); // setup headers

    useEffect(() => {
        // Invalidate responses from a previous brand context, including a cleared selection.
        invalidateExportSource();
        if (!requestsEnabled || !filteredRetailBrand?.length || !filteredLabelBrand?.length) return;

        resetDataStates({ resetColorEdits: true });
        getExportProductsConfigurationRequest();
    }, [requestsEnabled, filteredRetailBrand, filteredLabelBrand]); // get export products configuration

    useEffect(() => {
        if (allLabelBrandsList?.length === 1) {
            const copy = allLabelBrandsList.map((item, index) => ({
                ...item,
                isSelected: index === 0
            }));
            setFilteredLabelBrand(copy);
        }
    }, [allLabelBrandsList]); // pre-select filtered label brand if only one label brand is available

    useEffect(() => {
        setHasUnsavedChanges(saveButtonEnabled);
        setShowRulesSaved(!saveButtonEnabled);
    }, [saveButtonEnabled]); // set has unsaved changes
    // #endregion

    // #region COMPUTED PROPS
    const hasLabelBrandProducts = !!posExportRequestData?.labelBrandProducts?.length;
    const uniquePosGenderNames = [...new Set(posGenders.map(gender => getSourceValueKey(gender?.name)))];
    const uniqueMappedGenderNames = [
        ...new Set(genderMappings.map(mapping => getSourceValueKey(mapping?.originalGenderName)))
    ];
    const categorySourceRows = useMemo(
        () =>
            buildCategorySizeSourceRows({
                sourceItems:
                    (categoryFieldName === "Product type"
                        ? posExportRequestData?.labelBrandProductTypes
                        : posExportRequestData?.labelBrandCategories) || [],
                labelBrandProducts: posExportRequestData?.labelBrandProducts || [],
                genderMappings,
                categoryFieldName
            }),
        [categoryFieldName, posExportRequestData, genderMappings]
    );
    const labelBrandColors = posExportRequestData?.labelBrandColors || [];
    const savedSizeMappingConflicts = useMemo(
        () =>
            getSizeMappingConflicts({
                categoryFieldName,
                categorySourceRows,
                relatedSizeGroupMappings,
                labelBrandProducts: posExportRequestData?.labelBrandProducts,
                sizeGroupsFromPOS: posExportRequestData?.sizeGroupsFromPOS,
                mappings: sizeGroupsConvertionTable.flatMap(group =>
                    (Array.isArray(group?.sizes) ? group.sizes : []).map(size => ({
                        ...size,
                        sizeGroupName: group.sizeGroupName
                    }))
                )
            }),
        [
            categoryFieldName,
            categorySourceRows,
            relatedSizeGroupMappings,
            posExportRequestData,
            sizeGroupsConvertionTable
        ]
    );
    const hasCategorySizeCountIssues = useMemo(
        () =>
            getCategorySizeCountIssues({
                categoryFieldName,
                categoryMappings: (categorySourceRows || []).map(row => ({
                    labelBrandCategoryName: row?.name,
                    labelBrandGenderName: row?.gender,
                    labelBrandSizeGroupSizes: row.labelBrandSizeGroupSizes,
                    relatedSizeGroupNameFromPOS: findCategoryMapping(
                        relatedSizeGroupMappings,
                        row?.name,
                        row?.gender,
                        row.labelBrandSizeGroupSizes
                    )?.exportName
                })),
                labelBrandProducts: posExportRequestData?.labelBrandProducts,
                sizeGroupsFromPOS: posExportRequestData?.sizeGroupsFromPOS
            }).some(Boolean),
        [categoryFieldName, categorySourceRows, relatedSizeGroupMappings, posExportRequestData]
    );
    const categoryMappingsComplete =
        !categoryMappingsNeedConfirmation &&
        !hasCategorySizeCountIssues &&
        !!categoryFieldName &&
        !!categorySourceRows?.length &&
        categorySourceRows.every(row => {
            const categoryMapping = findCategoryMapping(
                categoryMappings,
                row?.name,
                row?.gender,
                row.labelBrandSizeGroupSizes
            );
            const relatedSizeGroupMapping = findCategoryMapping(
                relatedSizeGroupMappings,
                row?.name,
                row?.gender,
                row.labelBrandSizeGroupSizes
            );

            return (
                !!(categoryMapping?.completeCategoryNameFromPOS || categoryMapping?.exportName) &&
                hasConversionValue(relatedSizeGroupMapping?.exportName)
            );
        });

    const mappingCompletion = {
        seasons:
            posSeasons.length > 0 &&
            posSeasons.every(posSeason => {
                const mapping = seasonMappings.find(item => item?.labelBrandName === posSeason?.name);
                return mapping?.exportName !== null && mapping?.exportName !== undefined;
            }),
        genders:
            uniquePosGenderNames.length > 0 &&
            uniquePosGenderNames.every(name => uniqueMappedGenderNames.includes(name)),
        categories: categoryMappingsComplete,
        colors:
            !colorMappingsNeedConfirmation &&
            areColorsMapped(labelBrandColors, colorMappings, posExportRequestData?.colorsFromPOS),
        sizes:
            categoryMappingsComplete &&
            savedSizeMappingConflicts.size === 0 &&
            areMissingSizesMapped(
                getMissingSizeRows({
                    categoryFieldName,
                    categorySourceRows,
                    relatedSizeGroupMappings,
                    labelBrandProducts: posExportRequestData?.labelBrandProducts,
                    sizeGroupsFromPOS: posExportRequestData?.sizeGroupsFromPOS
                }),
                sizeGroupsConvertionTable
            )
    };
    // #endregion

    // #region RENDER

    return (
        <StyledPage className="pos-export-page" enablePageBreak={ENABLE_PAGE_BREAK}>
            <FullScreenLoader isLoading={isLoading} />

            <FullScreenOverlay show={isSaving}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    heading="SAVING..."
                    showCancel={false}
                    showConfirm={false}
                    loading={isSaving}
                />
            </FullScreenOverlay>

            <POSSeasonsTablePopup
                show={showSeasonsTablePopup}
                onClose={() => {
                    setShowSeasonsTablePopup(false);
                }}
                seasonMappings={seasonMappings}
                setSeasonMappings={setSeasonMappings}
                setSaveButtonEnabled={setSaveButtonEnabled}
                posExportRequestData={posExportRequestData}
            />

            <POSGendersTablePopup
                show={showGendersTablePopup}
                onClose={() => {
                    setShowGendersTablePopup(false);
                }}
                genderMappings={genderMappings}
                setGenderMappings={handleGenderMappingsChange}
                setSaveButtonEnabled={setSaveButtonEnabled}
                posExportRequestData={posExportRequestData}
            />

            <POSCategoriesTablePopup
                show={showCategoriesTablePopup}
                onClose={() => {
                    setShowCategoriesTablePopup(false);
                }}
                genderMappings={genderMappings}
                categoryFieldName={categoryFieldName}
                setCategoryFieldName={setCategoryFieldName}
                categoryMappings={categoryMappings}
                relatedSizeGroupMappings={relatedSizeGroupMappings}
                categoryMappingsNeedConfirmation={categoryMappingsNeedConfirmation}
                setCategoryMappings={handleCategoryMappingsChange}
                setSaveButtonEnabled={setSaveButtonEnabled}
                posExportRequestData={posExportRequestData}
                labelBrandID={filteredLabelBrand?.[0]?.value}
            />

            <POSColorsTablePopup
                key={colorContextKey}
                contextKey={colorContextKey}
                isContextCurrent={expectedContext => activeColorContextRef.current === expectedContext}
                show={showColorsTablePopup}
                onClose={() => {
                    setShowColorsTablePopup(false);
                }}
                colorMappings={colorMappings}
                colorMappingsNeedConfirmation={colorMappingsNeedConfirmation}
                setColorMappings={nextMappings => handleColorMappingsChange(nextMappings, colorContextKey)}
                setSaveButtonEnabled={setSaveButtonEnabled}
                posExportRequestData={posExportRequestData}
            />

            <POSSizeGroupConversionPopup
                show={showSizesTablePopup}
                onClose={() => setShowSizesTablePopup(false)}
                categoryFieldName={categoryFieldName}
                categorySourceRows={categorySourceRows}
                relatedSizeGroupMappings={relatedSizeGroupMappings}
                conversions={sizeGroupsConvertionTable}
                setConversions={setSizeGroupsConvertionTable}
                setSaveButtonEnabled={setSaveButtonEnabled}
                posExportRequestData={posExportRequestData}
            />

            {hideAllCards ? (
                <div className="no-label-brand-selected">
                    Select a label brand from the dropdown to set the export rules
                </div>
            ) : (
                <div className="frame-1000011192">
                    <div className="frame-1000011467">
                        <div className="frame-313379 w-100">
                            <POSExpRulesCard
                                brandName={filteredLabelBrand?.[0]?.name}
                                allLabelBrandsList={allLabelBrandsList}
                                selectedLabelBrand={filteredLabelBrand}
                                brandExportRules={brandExportRules}
                                setBrandExportRules={setBrandExportRules}
                                allPOSBrands={allPOSBrands}
                                setAllPOSBrands={setAllPOSBrands}
                                posHasMatch={posHasMatch}
                                formsEnabled={formsEnabled}
                                setFormsEnabled={setFormsEnabled}
                                setSaveButtonEnabled={setSaveButtonEnabled}
                                mappingCompletion={mappingCompletion}
                                hasLabelBrandProducts={hasLabelBrandProducts}
                                onManualBrandMatch={onManualBrandMatch}
                                setShowSeasonsTablePopup={setShowSeasonsTablePopup}
                                setShowGendersTablePopup={setShowGendersTablePopup}
                                setShowCategoriesTablePopup={setShowCategoriesTablePopup}
                                setShowColorsTablePopup={setShowColorsTablePopup}
                                onShowSizesClicked={() => setShowSizesTablePopup(true)}
                                sizesPopupEnabled={!!mappingCompletion.categories}
                                showRulesSaved={showRulesSaved}
                                setPosHasMatch={setPosHasMatch}
                                resetDataStates={resetDataStates}
                            />
                        </div>
                        <div className="frame-313378 w-100">
                            <POSExpPreviewCard
                                previewData={posPageData}
                                isProductRandom={isProductRandom}
                                brandExportRules={brandExportRules}
                                identifiers={identifiers}
                                allBrandItems={allBrandItems}
                                hasLabelBrandProducts={hasLabelBrandProducts}
                                onProductChange={getProductDataForPosExportRequest}
                                formsEnabled={formsEnabled}
                                descriptionsIncluded={descriptionsIncluded}
                                seasonMappings={seasonMappings}
                                genderMappings={genderMappings}
                                colorMappings={colorMappings}
                                categoryFieldName={categoryFieldName}
                                categoryMappings={categoryMappings}
                                relatedSizeGroupMappings={relatedSizeGroupMappings}
                                mappingCompletion={mappingCompletion}
                            />
                        </div>
                    </div>
                    <div className="frame-1000011466 w-100">
                        <POSExpInfoAvailableCard
                            posInfoData={posPageData}
                            formsEnabled={formsEnabled}
                            descriptionsIncluded={descriptionsIncluded}
                            setDescriptionsIncluded={setDescriptionsIncluded}
                            setSaveButtonEnabled={setSaveButtonEnabled}
                        />
                    </div>
                </div>
            )}
        </StyledPage>
    );
    // #endregion
}

// #region HEADERS
function setupHeaders(
    props,
    onClickRefreshAction,
    onSearch,
    onFiltersLoaded,
    allRetailBrandsList,
    onRetailBrandChange,
    filteredRetailBrand,
    allLabelBrandsList,
    onLabelBrandChange,
    filteredLabelBrand,
    saveButtonEnabled,
    onSaveClicked,
    productID,
    productName
) {
    const productIDSuffix = productID ? `?productID=${productID}&productName=${productName}` : "";

    props.setControlBarLeftBtns([
        {
            customSearch: (
                <CustomSearch enabled={false} placeholder="Search" highlighted={true} /* onTextChange={onSearch} */ />
            )
        }
    ]);
    props.setControlBarCenterBtns([
        { to: `/products${productIDSuffix}`, active: true, disabled: false, title: "LIVE" },
        { to: "/products/my-defaults/seasons", active: false, title: "MY DEFAULTS" }
    ]);
    props.setControlBarRightBtns([
        { to: `/products${productIDSuffix}`, active: false, disabled: false, title: "View", icon: "eye" },
        {
            to: `/products/stock${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Sales",
            icon: "bar-chart-05"
        },
        {
            to: `/products/metrics${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Metrics",
            icon: "bar-line-chart"
        },
        {
            to: `/products/details${productIDSuffix}`,
            active: false,
            disabled: false,
            title: "Details",
            icon: "microscope"
        },
        { to: "#", active: true, disabled: false, title: "Export", icon: "upload-01" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                onRefreshClick={onClickRefreshAction}
                layoutTypeOptions={{
                    gridEnabled: false,
                    listEnabled: false,
                    useLayoutButton: {
                        useInsteadOfGridList: true,
                        text: "SAVE",
                        enabled: saveButtonEnabled,
                        onClick: () => onSaveClicked()
                    }
                }}
                firstOrganize={{
                    visible: true,
                    dropdownData: allRetailBrandsList,
                    onDropdownChange: onRetailBrandChange,
                    disabled: allRetailBrandsList?.length === 1,
                    type: "singleSelect",
                    usePlaceholderText: "Select a retail brand",
                    selectedValue: filteredRetailBrand?.[0]?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allLabelBrandsList,
                    onDropdownChange: onLabelBrandChange,
                    disabled: false,
                    type: "singleSelect",
                    usePlaceholderText: "Select a label brand",
                    selectedValue: filteredLabelBrand?.[0]?.name
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
                settingsButton={{
                    visible: true,
                    enabled: false,
                    active: false,
                    outlined: false,
                    layoutComponent: <POSSetupSettingsMenuComponent />
                }}
            />
        )
    });
}
// #endregion
